import type { SalesforcePaymentsConfig } from '../types/config';
import type { PaymentRequest } from '../types/paymentRequest';
import type { VendorAccount } from '../types/paymentMethodSet';
import { Vendor } from '../types/paymentMethodSet';
import type { SfpStripe } from '../vendor/sfp-stripe';
import type { SfpVendor } from '../vendor/sfp-vendor';
import type { SfpPaypal } from '../vendor/sfp-paypal';
import type { SfpAdyen } from '../vendor/sfp-adyen';
import type SfpContext from '../sfp-context';

export function loadScriptAsync(
  url: string,
  async = true,
  type = 'text/javascript',
  integrity: string = undefined
): Promise<any> {
  return new Promise((resolve, reject) => {
    try {
      const scriptEle = document.createElement('script');
      scriptEle.type = type;
      scriptEle.async = async;
      scriptEle.src = url;
      if (integrity !== undefined) {
        scriptEle.integrity = integrity;
        scriptEle.crossOrigin = 'anonymous';
      }

      scriptEle.addEventListener('load', (_ev) => {
        resolve({ status: true });
      });

      scriptEle.addEventListener('error', (_ev) => {
        reject({
          status: false,
          message: `Failed to load the script from ` + url,
        });
      });

      document.body.appendChild(scriptEle);
    } catch (error) {
      reject(error);
    }
  });
}

export function loadCSS(url: string, integrity: string = undefined): Promise<any> {
  return new Promise((resolve, reject) => {
    try {
      const scriptEle = document.createElement('link');
      scriptEle.rel = 'stylesheet';
      scriptEle.href = url;
      if (integrity !== undefined) {
        scriptEle.integrity = integrity;
        scriptEle.crossOrigin = 'anonymous';
      }
      scriptEle.addEventListener('load', (_ev) => {
        resolve({ status: true });
      });

      scriptEle.addEventListener('error', (_ev) => {
        reject({
          status: false,
          message: `Failed to load the Css stylesheet ` + url,
        });
      });

      const temp = document.head.childNodes.item(0);
      document.head.insertBefore(scriptEle, temp);
    } catch (error) {
      reject(error);
    }
  });
}

export async function loadStripe(
  account: VendorAccount,
  context: SfpContext,
  config?: SalesforcePaymentsConfig,
  paymentRequest?: PaymentRequest
): Promise<SfpStripe> {
  const { loadStripeModule } = await import(/* webpackChunkName: "stripe" */ '../vendor/sfp-stripe');
  return loadStripeModule(account, context, config, paymentRequest);
}

export async function loadPaypal(
  account: VendorAccount,
  context: SfpContext,
  config?: SalesforcePaymentsConfig,
  paymentRequest?: PaymentRequest
): Promise<SfpPaypal> {
  const { loadPaypalModule } = await import(/* webpackChunkName: "paypal" */ '../vendor/sfp-paypal');
  return loadPaypalModule(account, context, config, paymentRequest);
}

export async function loadAdyen(
  account: VendorAccount,
  context: SfpContext,
  config?: SalesforcePaymentsConfig,
  paymentRequest?: PaymentRequest
): Promise<SfpAdyen> {
  const { loadAdyenModule } = await import(/* webpackChunkName: "adyen" */ '../vendor/sfp-adyen');
  return loadAdyenModule(account, context, config, paymentRequest);
}

export async function getVendorHandler(
  account: VendorAccount,
  context: SfpContext,
  config?: SalesforcePaymentsConfig,
  paymentRequest?: PaymentRequest
): Promise<SfpVendor> {
  if (account.vendor === Vendor.STRIPE) {
    return await loadStripe(account, context, config, paymentRequest);
  } else if (account.vendor === Vendor.PAYPAL) {
    return await loadPaypal(account, context, config, paymentRequest);
  } else if (account.vendor === Vendor.ADYEN) {
    return await loadAdyen(account, context, config, paymentRequest);
  } else {
    console.error(`Vendor ${account.vendor} not supported`);
    context.report({
      code: 'VENDOR_NOT_FOUND',
      vendor: account.vendor,
      type: 'PAYMENT_METHOD_SET',
      message: `Vendor ${account.vendor} not supported`,
    });
    throw new Error(`Vendor ${account.vendor} not supported`);
  }
}
